import { createTheme, alpha } from '@mui/material';

const shape = {
  borderRadius: 12,
};

const typography = {
  fontFamily:
    "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif",
  h5: {
    fontWeight: 600,
  },
  h6: {
    fontWeight: 600,
  },
  subtitle1: {
    fontWeight: 500,
  },
  button: {
    fontWeight: 600,
    textTransform: 'none' as const,
  },
};

export const appTheme = createTheme({
  shape,
  typography,
  palette: {
    mode: 'light',
    primary: {
      main: '#4338CA',
      light: '#6366F1',
      dark: '#3730A3',
    },
    secondary: {
      main: '#0891B2',
      light: '#22D3EE',
      dark: '#0E7490',
    },
    success: {
      main: '#16A34A',
    },
    warning: {
      main: '#D97706',
    },
    error: {
      main: '#DC2626',
    },
    info: {
      main: '#2563EB',
    },
    background: {
      default: '#F1F5F9',
      paper: '#FFFFFF',
    },
    divider: alpha('#94A3B8', 0.2),
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F1F5F9',
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderBottom: '1px solid',
          borderColor: alpha('#94A3B8', 0.2),
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '6px 16px',
        },
        contained: {
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha('#4338CA', 0.3)}`,
          },
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation0: {
          border: '1px solid',
          borderColor: alpha('#94A3B8', 0.2),
        },
        elevation1: {
          boxShadow: `0 1px 3px ${alpha('#0F172A', 0.08)}, 0 1px 2px ${alpha('#0F172A', 0.06)}`,
        },
        elevation3: {
          boxShadow: `0 4px 16px ${alpha('#0F172A', 0.1)}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid',
          borderColor: alpha('#94A3B8', 0.2),
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: `0 20px 60px ${alpha('#0F172A', 0.15)}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: '0.75rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: alpha('#4338CA', 0.08),
            '&:hover': {
              backgroundColor: alpha('#4338CA', 0.12),
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
  },
});

export const appThemeDark = createTheme({
  shape,
  typography,
  palette: {
    mode: 'dark',
    primary: {
      main: '#818CF8',
      light: '#A5B4FC',
      dark: '#6366F1',
    },
    secondary: {
      main: '#22D3EE',
      light: '#67E8F9',
      dark: '#06B6D4',
    },
    success: {
      main: '#4ADE80',
    },
    warning: {
      main: '#FBBF24',
    },
    error: {
      main: '#F87171',
    },
    info: {
      main: '#60A5FA',
    },
    background: {
      default: '#0F172A',
      paper: '#1E293B',
    },
    divider: alpha('#94A3B8', 0.15),
    contrastThreshold: 3,
    tonalOffset: 0.2,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#0F172A',
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderBottom: '1px solid',
          borderColor: alpha('#94A3B8', 0.15),
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '6px 16px',
        },
        contained: {
          '&:hover': {
            boxShadow: `0 4px 12px ${alpha('#818CF8', 0.3)}`,
          },
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation0: {
          border: '1px solid',
          borderColor: alpha('#94A3B8', 0.15),
        },
        elevation1: {
          boxShadow: `0 1px 3px ${alpha('#000000', 0.2)}, 0 1px 2px ${alpha('#000000', 0.15)}`,
        },
        elevation3: {
          boxShadow: `0 4px 16px ${alpha('#000000', 0.25)}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid',
          borderColor: alpha('#94A3B8', 0.15),
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: `0 20px 60px ${alpha('#000000', 0.4)}`,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 6,
          fontSize: '0.75rem',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontWeight: 500,
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: alpha('#818CF8', 0.12),
            '&:hover': {
              backgroundColor: alpha('#818CF8', 0.18),
            },
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
  },
});
